import React, {Fragment} from 'react';

import cs from 'classnames';
import {Link} from 'react-router';

import {DocumentActionsButton} from 'app/components/general/DocumentActionsButton';
import {
  StyledCompactItemHeaderDiv,
  StyledCompactItemHeaderSpan,
} from 'app/general/bootstrap/utils';
import {DocIsDraftBadge} from 'app/general/dashboards/DocIsDraftBadge';
import {DocNoViewBadge} from 'app/general/dashboards/DocNoViewBadge';
import {DocUnsignedBadge} from 'app/general/dashboards/DocUnsignedBadge';
import {ItemLatestActivity} from 'app/general/dashboards/ItemLastActivity';
import {
  type BadgeRelation,
  ItemRelationsBadges,
} from 'app/general/dashboards/ItemRelationsBadges';
import {ItemRelationsListOfGenerics} from 'app/general/dashboards/ItemRelationsListOfGenerics';
import {ItemRelationsListOfTeams} from 'app/general/dashboards/ItemRelationsListOfTeams';
import {ItemRelationsLists} from 'app/general/dashboards/ItemRelationsLists';
import {DocumentPrivateStatusIcon} from 'app/general/doc-status';
import {CheckboxButton} from 'app/general/widgets/CheckboxButton';
import {DocumentIconWithNotifications} from 'app/notifly/components/DocumentIconWithNotifications';
import {NotificationType} from 'app/notifly/types';
import {DashboardItemPresentMembers} from 'app/presence/components/DashboardItemPresentMembers';
import {StyledDocItem} from 'app/procedures/styles';
import {DocNeedsReviewBadge} from 'app/review/components/DocNeedReviewBadge';
import {getContentType} from 'app/utils/content-type';
import {getObjectName} from 'app/utils/fallbacks';

import type {
  ContentType,
  DocumentContentType,
  DocumentSerializedType,
  RelatedFolderSerializedType,
  SignoffSerializedType,
} from 'sweet-types';

const relations: Array<BadgeRelation> = [
  {
    ct: 'tag',
    getter: (p: DocumentSerializedType['document']) => p.tags,
    Component: ItemRelationsListOfGenerics,
  },
  {
    ct: 'team',
    getter: (p: DocumentSerializedType['document']) =>
      p.team_memberships.map((team) => team.team),
    Component: ItemRelationsListOfTeams,
  },
];

type Props = {
  object: DocumentSerializedType;
  contentType: DocumentContentType;
  sortBy?: string;
  refreshData: () => unknown;
  activeRelations: Record<ContentType, boolean>;
  toggleRelation: (contentType: ContentType, enabled: boolean) => unknown;
  notifications?: Array<NotificationType>;
  disableSortBy?: boolean;
  currentFolder?: string;
  className?: string;
  mySignoffs: Array<SignoffSerializedType> | null;
  isSelected: boolean;
  addToSelection: (doc: DocumentSerializedType) => unknown;
  removeFromSelection: (doc: DocumentSerializedType) => unknown;
  enableBulkSelect: boolean;
};

export const CompactListItem = ({
  object,
  contentType,
  refreshData,
  sortBy,
  activeRelations,
  notifications,
  disableSortBy,
  toggleRelation,
  currentFolder,
  className = '',
  mySignoffs,
  isSelected,
  addToSelection,
  enableBulkSelect,
  removeFromSelection,
}: Props): JSX.Element => {
  const signoff = filterSignoffs(
    {
      object_id: object.document.id,
      content_type: contentType,
    },
    mySignoffs
  );

  function onSelectDoc(isChecked: boolean) {
    if (isChecked) {
      addToSelection(object);
    } else {
      removeFromSelection(object);
    }
  }

  return (
    <StyledDocItem
      className={cs(
        'row border-bottom py-2 d-flex align-items-center position-relative',
        className,
        {'opacity-overlay': object.document.approved_at == null}
      )}
    >
      <DashboardItemPresentMembers
        contentType={contentType}
        objectId={object.id}
      />
      <StyledCompactItemHeaderDiv className="flex-column">
        <div className="d-flex align-self-start">
          {enableBulkSelect && (
            <CheckboxButton
              isChecked={isSelected}
              onClick={(isChecked) => onSelectDoc(isChecked)}
              useRealCheckbox={true}
              className="mr-3 btn btn-sm btn-link"
            />
          )}
          <Link
            to={object.html_url}
            className="text-dark font-weight-bold d-block d-flex w-100"
          >
            <StyledCompactItemHeaderSpan>
              <DocumentIconWithNotifications
                contentType={contentType}
                notifications={notifications}
              />

              {getObjectName(object, contentType, true)}
              <>
                <DocIsDraftBadge approved_at={object.document.approved_at} />
                <DocNoViewBadge can_view={object.can_view} />
                {object.document.next_review_at != null ? (
                  <DocNeedsReviewBadge
                    reviewDate={object.document.next_review_at}
                  />
                ) : null}
                <DocUnsignedBadge signoff={signoff} />
              </>
            </StyledCompactItemHeaderSpan>
          </Link>
        </div>
        {object.folder != null && currentFolder !== object.folder.hashid ? (
          <small className="text-muted d-block w-100">
            {createFolderBreadcrumb(object.folder, currentFolder).map(
              (folder) => FolderBreadcrumbLink(folder)
            )}
            {getObjectName(object, contentType, true)}
          </small>
        ) : null}
      </StyledCompactItemHeaderDiv>

      <ItemRelationsBadges
        className="col-auto ml-auto ml-sm-0 order-lg-3"
        relations={relations}
        object={object.document}
        activeRelations={activeRelations}
        onToggle={toggleRelation}
      />

      {!disableSortBy ? (
        <div className="col col-sm-6 col-lg-3 mr-auto order-lg-4">
          <ItemLatestActivity sortBy={sortBy} object={object.document} />
        </div>
      ) : null}

      {object.private === false ? (
        <div className="col-auto order-lg-2 pr-0">
          <DocumentPrivateStatusIcon ct={contentType} isPrivate={false} />
        </div>
      ) : null}

      <div className="col-auto order-lg-5">
        <DocumentActionsButton
          document={object}
          pageContext="dashboard"
          currentFolder={currentFolder}
          refreshData={refreshData}
        />
      </div>

      <div className="col-12 order-last">
        <ItemRelationsLists
          object={object.document}
          activeRelations={activeRelations}
          relations={relations}
        />
      </div>
    </StyledDocItem>
  );
};

const FolderBreadcrumbLink = (folder: RelatedFolderSerializedType) => {
  return (
    <Fragment key={folder.id}>
      <Link to={folder.html_url} className="text-muted">
        <i className={`fa-fw ${getContentType('folder').icon}`} /> {folder.name}
      </Link>{' '}
      / &nbsp;
    </Fragment>
  );
};

const createFolderBreadcrumb = (
  folder: RelatedFolderSerializedType,
  currentFolder?: string
): Array<RelatedFolderSerializedType> => {
  const parentFolders: Array<RelatedFolderSerializedType> = [];
  let loopFolder: RelatedFolderSerializedType = folder;
  parentFolders.push(loopFolder);

  while (loopFolder.parent != null) {
    loopFolder = loopFolder.parent;
    parentFolders.push(loopFolder);
  }

  let canRender = false;

  if (currentFolder == null) {
    canRender = true;
  }

  const toReturnList: Array<RelatedFolderSerializedType> = [];

  for (let i = parentFolders.length - 1; i >= 0; i -= 1) {
    if (canRender) {
      toReturnList.push(parentFolders[i]);
    } else {
      if (currentFolder === parentFolders[i].hashid) {
        canRender = true;
      }
    }
  }

  return toReturnList;
};

export const filterSignoffs = (
  {
    object_id,
    content_type,
  }: {object_id: number; content_type: DocumentContentType},
  signoffs: Array<SignoffSerializedType> | null
) => {
  if (signoffs == null) {
    return null;
  }

  return signoffs.find(
    (signoff) =>
      signoff.object_id === object_id && signoff.content_type === content_type
  );
};
